/**
 * Sticky ATC Drawer - Sheet-style sticky add to cart
 * Desktop: Floating card in bottom-right
 * Mobile: Bottom sheet drawer with collapsed/expanded states
 */
/* global olark */
if (!customElements.get('sticky-atc-drawer')) {
  customElements.define(
    'sticky-atc-drawer',
    class StickyAtcDrawer extends HTMLElement {
      constructor() {
        super();
        this.isExpanded = false;
        this.isUpdating = false;
        this.chatIsOpen = false;
        // Single source of truth for "mobile", matching the CSS band exactly
        // (mobile ends at 767.98px). The old innerWidth <= 768 / < 768 checks
        // disagreed with the CSS and with each other at exactly 768px.
        this.mobileMQ = window.matchMedia('(max-width: 767.98px)');
        document.body.classList.add('sticky-atc-drawer-enabled');
      }

      /** Safely check if Olark is loaded and callable */
      isOlarkReady() {
        return typeof olark === 'function';
      }

      get variantIdInput() {
        return this.querySelector('[name="id"]');
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.container = this.closest('.sticky-atc-drawer');
        this.productId = this.dataset.productId;
        this.dismissedKey = `sticky-atc-drawer-dismissed-${this.productId}`;

        // Check if user has dismissed this drawer
        if (sessionStorage.getItem(this.dismissedKey)) {
          this.container.style.display = 'none';
          return;
        }

        this.variantCount = this.getVariantCount();
        this.init();
        this.initDrawerBehavior();
        this.initCloseButton();
        this.initEscapeKey();
        this.initChatButton();
        this.initVariantButtons();
        this.initQuantitySync();
        this.syncWithMainProductForm();

        // The collapsed Add button is server-rendered disabled for V2 multi-variant
        // products (sticky-atc-bar.liquid `await_variant_picker`) so a dead drawer
        // script can't submit the first-available variant. Enable it only here, at the
        // end of a fully successful init: the mobile expand-intercept (initDrawerBehavior)
        // and variant sync are now wired, so on mobile the button expands the picker and
        // on desktop it submits the synced variant. If any init step above threw, this
        // line is skipped and the button stays safely disabled.
        const gatedAddBtn = this.container?.querySelector('.sticky-atc-drawer__cart-btn[data-await-picker]');
        if (gatedAddBtn) {
          gatedAddBtn.removeAttribute('disabled');
          gatedAddBtn.removeAttribute('data-await-picker');
        }
      }

      getVariantCount() {
        return parseInt(this.container.dataset.variantCount, 10) || 0;
      }

      // The collapsed bar and the expanded drawer each render their own
      // quantity-input, both associated with the same drawer form via
      // `form="..."` (they live outside the actual <form> element). Since
      // both share `name="quantity"`, the form submits BOTH values on add —
      // whichever one wins server-side silently overrides the other, so a
      // quantity set in one control (typically the one the user actually
      // touched) can be discarded in favor of the other's default value.
      // Keep them mirrored so both always agree, regardless of which the
      // browser/server treats as authoritative for the duplicate key.
      initQuantitySync() {
        const inputs = Array.from(this.container?.querySelectorAll('input[name="quantity"]') || []);
        if (inputs.length < 2) return;

        inputs.forEach((input) => {
          input.addEventListener('change', () => {
            inputs.forEach((other) => {
              if (other !== input) other.value = input.value;
            });
          });
        });
      }

      init() {
        if (!this.mainProductInfo) {
          this.container.classList.add('sticky-atc-drawer--show');
          this.handleDrawerVisibilityChange(true);
          return;
        }

        this.checkDevice();
        window.addEventListener('resize', this.checkDevice.bind(this));

        // Mobile: always visible, no scroll trigger. Desktop: scroll-gated.
        // Re-applied when a rotation/resize crosses the breakpoint, so the
        // mode can't get stuck on the wrong side of the boundary.
        const applyMode = () => {
          if (this.mobileMQ.matches) {
            if (this.observer) {
              this.observer.disconnect();
              this.observer = null;
            }
            this.container.classList.add('sticky-atc-drawer--show');
            this.handleDrawerVisibilityChange(true);
          } else {
            this.setupScrollObserver();
          }
        };
        applyMode();
        this.mobileMQ.addEventListener('change', applyMode);
      }

      setupScrollObserver() {
        if (this.observer) return;

        // Find the element to observe - prefer media gallery for earlier trigger
        const mediaGallery = document.querySelector('.product__media-gallery, media-gallery');
        const observeTarget = mediaGallery || this.mainProductInfo;

        // Show drawer when user scrolls past the observed element
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const isVisible = !entry.isIntersecting;
              // Don't hide drawer while chat is open (prevents layout shift issues)
              if (!isVisible && this.chatIsOpen) {
                return;
              }
              this.container.classList.toggle('sticky-atc-drawer--show', isVisible);
              this.handleDrawerVisibilityChange(isVisible);
            });
          },
          { threshold: 0, rootMargin: '0px' }
        );
        this.observer.observe(observeTarget);
      }

      handleDrawerVisibilityChange(isVisible) {
        // Hide Olark widget on mobile when sticky drawer is visible
        if (!this.mobileMQ.matches) return;

        if (this.isOlarkReady()) {
          try {
            if (isVisible) {
              olark('api.box.hide');
            } else {
              olark('api.box.show');
            }
          } catch (_e) { /* Olark not fully initialized */ }
        }
      }

      checkDevice() {
        document.documentElement.style.setProperty(
          '--sticky-atc-drawer-height',
          this.container.offsetHeight + 'px'
        );
      }

      initDrawerBehavior() {
        // Handle click
        const handle = this.container.querySelector('.sticky-atc-drawer__handle');
        if (handle) {
          handle.addEventListener('click', () => this.toggleExpanded());
        }

        // Collapsed Add button expands on mobile. Bound unconditionally and
        // gated at click time so a rotation across the breakpoint doesn't
        // leave the wrong behavior wired in.
        const collapsedAddBtn = this.container.querySelector('.sticky-atc-drawer__collapsed .btn');
        if (collapsedAddBtn) {
          collapsedAddBtn.addEventListener('click', (e) => {
            // Only intercept if we have variants to select
            if (this.mobileMQ.matches && this.hasMultipleVariants() && !this.isExpanded) {
              e.preventDefault();
              e.stopPropagation();
              this.toggleExpanded(true);
            }
          });
        }
      }

      hasMultipleVariants() {
        return this.variantCount > 1;
      }

      toggleExpanded(forceExpand = null) {
        this.isExpanded = forceExpand !== null ? forceExpand : !this.isExpanded;
        this.container.classList.toggle('sticky-atc-drawer--expanded', this.isExpanded);
        this.checkDevice();
      }

      initCloseButton() {
        const closeButton = this.container.querySelector('.sticky-atc-drawer__close');
        if (closeButton) {
          closeButton.addEventListener('click', () => {
            if (this.isExpanded) {
              this.toggleExpanded(false);
            } else {
              this.dismiss();
            }
          });
        }
      }

      initEscapeKey() {
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.isExpanded) {
            this.toggleExpanded(false);
          }
        });
      }

      initChatButton() {
        const chatButton = this.container.querySelector('.sticky-atc-drawer__chat-btn');
        if (!chatButton) return;

        const self = this;

        // Track when chat is opened from sticky drawer
        chatButton.addEventListener('click', (e) => {
          e.preventDefault();
          self.chatIsOpen = true;

          // Manually trigger Olark expand since we prevented default
          if (this.isOlarkReady()) {
            try { olark('api.box.expand'); } catch (_e) { /* Olark not fully initialized */ }
          }

          // Try to register Olark callback (may not be available yet)
          self.registerOlarkCallback();

          // Fallback: Reset after 30 seconds if Olark callback never fires
          clearTimeout(self.chatResetTimeout);
          self.chatResetTimeout = setTimeout(() => {
            if (self.chatIsOpen) {
              self.chatIsOpen = false;
            }
          }, 30000);
        });

        // Try to register Olark callback now (might work if already loaded)
        this.registerOlarkCallback();
      }

      registerOlarkCallback() {
        if (this.olarkCallbackRegistered) return;

        if (this.isOlarkReady()) {
          try {
            const self = this;
            olark('api.box.onShrink', () => {
              self.chatIsOpen = false;
              clearTimeout(self.chatResetTimeout);
            });
            this.olarkCallbackRegistered = true;
          } catch (_e) { /* Olark not fully initialized */ }
        }
      }

      dismiss() {
        sessionStorage.setItem(this.dismissedKey, 'true');
        this.container.classList.remove('sticky-atc-drawer--show');
        this.container.style.display = 'none';
        this.handleDrawerVisibilityChange(false);
        if (this.observer) {
          this.observer.disconnect();
        }
      }

      initVariantButtons() {
        // Color swatches
        this.container.querySelectorAll('.sticky-atc-drawer__color-swatch').forEach((swatch) => {
          swatch.addEventListener('click', () => {
            if (swatch.classList.contains('is-disabled')) return;
            this.handleVariantOptionClick(swatch, 'color');
          });
        });

        // Size buttons
        this.container.querySelectorAll('.sticky-atc-drawer__size-btn').forEach((btn) => {
          btn.addEventListener('click', () => {
            if (btn.classList.contains('is-disabled')) return;
            this.handleVariantOptionClick(btn, 'size');
          });
        });
      }

      handleVariantOptionClick(element, type) {
        const optionName = element.dataset.optionName;
        const optionValue = element.dataset.optionValue;

        if (!optionName || !optionValue) return;

        // Update active state in drawer
        const siblings = element.parentElement.querySelectorAll(
          type === 'color' ? '.sticky-atc-drawer__color-swatch' : '.sticky-atc-drawer__size-btn'
        );
        siblings.forEach((s) => s.classList.remove('is-active'));
        element.classList.add('is-active');

        // Sync with main product form
        this.syncOptionToMainForm(optionName, optionValue);
      }

      syncOptionToMainForm(optionName, optionValue) {
        if (!this.mainProductInfo) return;

        // Try V2 variant selector first
        const sectionId = this.mainProductInfo.dataset.section;
        const v2Selector = document.getElementById(`v2-variant-selector-${sectionId}`);

        if (v2Selector) {
          // Find the matching dimension and option in V2 selector
          const dimension = v2Selector.querySelector(
            `.v2-dimension[data-dimension-name="${optionName}"], .v2-dimension[data-dimension-label="${optionName}"]`
          );
          if (dimension) {
            const option = dimension.querySelector(
              `[data-option-value="${optionValue}"], input[value="${optionValue}"]`
            );
            if (option) {
              if (option.tagName === 'INPUT') {
                option.checked = true;
                option.dispatchEvent(new Event('change', { bubbles: true }));
              } else {
                option.click();
              }
              return;
            }
          }
        }

        // Fallback to classic variant-selects
        const variantSelects = this.mainProductInfo.querySelector('variant-selects');
        if (variantSelects) {
          const selects = variantSelects.querySelectorAll('select');
          selects.forEach((select) => {
            const label = select.closest('.select')?.querySelector('label')?.textContent?.trim();
            if (label && label.toLowerCase().includes(optionName.toLowerCase())) {
              select.value = optionValue;
              select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });

          const radios = variantSelects.querySelectorAll('input[type="radio"]');
          radios.forEach((radio) => {
            if (radio.value === optionValue) {
              const fieldset = radio.closest('fieldset');
              const legend = fieldset?.querySelector('legend')?.textContent?.trim();
              if (legend && legend.toLowerCase().includes(optionName.toLowerCase())) {
                radio.checked = true;
                radio.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          });
        }
      }

      syncWithMainProductForm() {
        // Listen for variant changes from main product form.
        // Guard against race where connectedCallback fires before FoxTheme.pubsub
        // initializes. The v2:variantChange listener below is the modern path and
        // covers variant updates regardless of pubsub availability.
        if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
          FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
            if (!this.mainProductInfo) return;
            const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
            if (!isMainProduct) return;

            const variant = event.data.variant;
            this.handleVariantUpdate(variant);
          });
        }

        // Also listen for V2 variant changes.
        // Defer DOM work off the click frame so it doesn't land on INP.
        document.addEventListener('v2:variantChange', (e) => {
          if (!e.detail?.variant) return;
          const variant = e.detail.variant;
          const apply = () => this.handleVariantUpdate(variant);
          if (typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(apply);
          } else {
            apply();
          }
        });
      }

      handleVariantUpdate(variant) {
        if (!variant) {
          this.setSelectOptionsState();
          return;
        }

        this.currentVariant = variant;

        // Update hidden variant input
        if (this.variantIdInput) {
          this.variantIdInput.value = variant.id;
        }

        // Update images
        this.updateImages(variant);

        // Update price
        this.updatePrice(variant);

        // Update variant text
        this.updateVariantText(variant);

        // Update button state
        this.updateButton(variant);

        // Update active states on variant options
        this.updateActiveOptions(variant);
      }

      updateImages(variant) {
        const thumbImages = this.container.querySelectorAll(
          '.sticky-atc-drawer__image > img, .sticky-atc-drawer__expanded-image img'
        );
        const expandedImages = this.container.querySelectorAll(
          '.sticky-atc-drawer__image-expanded img'
        );

        let newSrc = null;

        // Priority 1: Custom media config
        const customMediaConfig = this.getCustomMediaConfig(variant);
        if (customMediaConfig?.hasCustomMedia && customMediaConfig.mediaObjects?.length > 0) {
          newSrc = customMediaConfig.mediaObjects[0].external_url;
        }

        // Priority 2: Variant featured image
        if (!newSrc && variant.featured_image) {
          newSrc = variant.featured_image.src;
        }

        if (newSrc) {
          // Update thumbnail images
          thumbImages.forEach((img) => {
            img.src = this.getSizedImageUrl(newSrc, 180);
            img.alt = variant.title || '';
          });

          // Update expanded preview images (larger size)
          expandedImages.forEach((img) => {
            img.src = this.getSizedImageUrl(newSrc, 560);
            img.alt = variant.title || '';
          });
        }
      }

      getCustomMediaConfig(variant) {
        if (!variant?.id) return null;
        const configElement = document.getElementById(`custom-media-config-${variant.id}`);
        if (!configElement) return null;
        try {
          return JSON.parse(configElement.textContent);
        } catch (_e) {
          return null;
        }
      }

      getSizedImageUrl(url, width) {
        if (!url) return '';
        if (url.includes('/cdn/shop/') || url.includes('cdn.shopify.com')) {
          const baseUrl = url.replace(/[?&]width=\d+/g, '').replace(/\?$/, '');
          const separator = baseUrl.includes('?') ? '&' : '?';
          return `${baseUrl}${separator}width=${width}`;
        }
        return url;
      }

      updatePrice(variant) {
        const priceContainers = this.container.querySelectorAll('.f-price');
        const moneyFormat = FoxTheme.settings.moneyFormat;

        priceContainers.forEach((priceWrapper) => {
          // price.liquid renders TWO .f-price-item--regular elements: the
          // visible current price inside .f-price__regular (shown when NOT
          // on sale), and the strikethrough compare-at price nested inside
          // .f-price__sale (shown only when on sale). An unscoped
          // querySelector('.f-price-item--regular') always grabbed the first
          // (non-sale) one regardless of which branch is actually visible —
          // blanking the visible price on non-sale variants and leaving the
          // visible strikethrough stale on sale variants. Scope both.
          const regularPrice = priceWrapper.querySelector('.f-price__regular .f-price-item--regular');
          const salePrice = priceWrapper.querySelector('.f-price__sale .f-price-item--sale');
          const compareAtPrice = priceWrapper.querySelector('.f-price__sale .f-price-item--regular');

          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            priceWrapper.classList.add('f-price--on-sale');
            if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(variant.price, moneyFormat);
            if (compareAtPrice) compareAtPrice.innerHTML = `<s>${FoxTheme.Currency.formatMoney(variant.compare_at_price, moneyFormat)}</s>`;
          } else {
            priceWrapper.classList.remove('f-price--on-sale');
            if (regularPrice) regularPrice.innerHTML = FoxTheme.Currency.formatMoney(variant.price, moneyFormat);
          }

          priceWrapper.classList.toggle('f-price--sold-out', !variant.available);
        });
      }

      updateVariantText(variant) {
        const variantTexts = this.container.querySelectorAll(
          '.sticky-atc-drawer__variant, .sticky-atc-drawer__expanded-variant'
        );
        const optionText = variant.options?.join(' / ') || variant.title;
        // Hide "Default Title" for single-variant products
        const isDefaultTitle = optionText === 'Default Title';
        variantTexts.forEach((el) => {
          el.textContent = isDefaultTitle ? '' : optionText;
          el.style.display = isDefaultTitle ? 'none' : '';
        });
      }

      updateButton(variant) {
        const buttons = this.container.querySelectorAll('[name="add"]');
        buttons.forEach((btn) => {
          const textEl = btn.querySelector('.btn__text, span');
          if (variant.available) {
            btn.removeAttribute('disabled');
            btn.classList.remove('btn--select-options');
            if (textEl) textEl.textContent = FoxTheme.variantStrings.addToCart;
          } else {
            btn.setAttribute('disabled', 'disabled');
            if (textEl) textEl.textContent = FoxTheme.variantStrings.soldOut;
          }
        });
      }

      updateActiveOptions(variant) {
        if (!variant.options) return;

        // Update color swatches
        this.container.querySelectorAll('.sticky-atc-drawer__color-swatch').forEach((swatch) => {
          const optionValue = swatch.dataset.optionValue;
          const isActive = variant.options.includes(optionValue);
          swatch.classList.toggle('is-active', isActive);
        });

        // Update size buttons
        this.container.querySelectorAll('.sticky-atc-drawer__size-btn').forEach((btn) => {
          const optionValue = btn.dataset.optionValue;
          const isActive = variant.options.includes(optionValue);
          btn.classList.toggle('is-active', isActive);
        });
      }

      setSelectOptionsState() {
        const buttons = this.container.querySelectorAll('[name="add"]');
        buttons.forEach((btn) => {
          const textEl = btn.querySelector('.btn__text, span');
          btn.classList.add('btn--select-options');

          const firstDimLabel = this.getFirstUnselectedDimension();
          if (firstDimLabel) {
            btn.removeAttribute('disabled');
            if (textEl) {
              const selectText = FoxTheme?.variantStrings?.selectToAdd || 'Select';
              textEl.textContent = `${selectText} ${firstDimLabel}`;
            }
          } else {
            btn.setAttribute('disabled', 'disabled');
            if (textEl) textEl.textContent = FoxTheme?.variantStrings?.selectOptions || 'Select Options';
          }
        });
      }

      getFirstUnselectedDimension() {
        const sectionId = this.mainProductInfo?.dataset?.section;
        const variantSelector = document.getElementById(`v2-variant-selector-${sectionId}`);

        if (variantSelector) {
          const firstDimLabel = variantSelector.querySelector('.v2-dimension .v2-dimension__name');
          if (firstDimLabel) {
            return firstDimLabel.textContent.replace(':', '').trim();
          }
        }
        return null;
      }
    }
  );
}
